// External
import _ from "lodash";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import PropTypes from "prop-types";
import update from "immutability-helper";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classNames from "classnames";

// Custom hooks
import { useHandleScroll } from "Utils/CustomHooks";
import { useMount } from "Hooks";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import MarkdownView from "UIComponents/MarkdownView";
import InputTextField from "UIComponents/InputTextField";
import ConfigureAIVoice from "AppComponents/ConfigureAIVoice";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import AIContextBar from "AppComponents/AIContextBar";
import PromptSuggestionModal from "./components/PromptSuggestionModal";
import PrerequisiteSelection from "./components/PrerequisiteSelection";
import NewTag from "./components/NewTag";
import PromptAction from "./components/PromptAction";
import QuestionFieldComponent from "Worksheet/components/Question/QuestionFieldComponent";

// Design system
import {
  IconButton,
  Avatar,
  Button,
  Tooltip,
  TextAreaInput,
  Modal,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Checkbox,
} from "@toddle-edu/ds-web";
import {
  RefreshArrowOutlined,
  SendFilled,
  OpenBookStarOutlined,
  Shifu3D,
  CloseOutlined,
  PencilLineOutlined,
  TickLargeOutlined,
  RightArrowOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import { colors, fontStyles, elevations } from "@toddle-edu/ds-theme";

// Scss
import classes from "./AiQuestionAssistant.scss";

// Utils / Services / Constants
import {
  hideIntercom,
  showIntercom,
  resetAIConversation,
  getVariablesForLocalisedPlannerTemplates,
  updateAIConversation,
} from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import {
  checkScrollAtBottom,
  prepareLoadMoreFeed,
  scrollIntoView,
  getStyleStrippedText,
} from "Utils";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import {
  updateAssessmentTemplate,
  getUnitDetailsFromUnitPlanField,
  getFieldValue,
  combineUnitPlanMultiFieldData,
  getUnitPlanFromAssessment,
  updateTemplateForLearningGoals,
} from "Assessments/modules/Utils";
import {
  getBlockActions,
  getCombinedPrompts,
  getIsAIResponseLoading,
  getIsListTitleBold,
  getTemplateArray,
  getEmojisAndTextFromString,
  checkIsLEAIUserMessageEditEnabled,
  getEnhancePromptsOptions,
  getQuestionActionOptions,
  getWorksheetAssistantContainerStyle,
  getToolTypeFromContent,
  getEnhanceOptions,
  getTypeOfQuestionFromContent,
  isConvertToAlternateAction,
  getAlternateQuestionTypes,
  isAssessmentToolsAlreadyExist,
  getImprovePrompts,
  getUpdatedPrompts,
} from "./modules/Utils";
import { ASSESSMENT, WRITING, LOADING } from "Constants/stringConstants";
import {
  EMOJI_COLOR_TRAY,
  DIALOGS_INFO,
  CONVERT_QUESTION_FORMAT,
  QUESTION_TYPE_UID_VALUE_MAPPING,
  CF_NEW_QUESTION_TYPE,
  CF_TYPE_OF_QUESTION,
  GENERATE_QUESTIONS,
  CF_QUESTION_TEXT,
  WS_AI_SETTING_FLAGS,
} from "./modules/Constants";
import {
  AI_TRANSLATION_LANGUAGES,
  ASSESSMENT_TOOLS_OBJ,
} from "@toddle-edu/common-utils";
import { updateUnitPlanTemplate } from "./components/PrerequisiteSelection/Utils";
import {
  formatAiFillResponse,
  getAssessmentQuestionsQueryVariables,
  getLearningGoalsFields,
  isFillInTheBlankRes,
  replaceLearningGoalsFields,
  getAiConversationId,
} from "Worksheet/modules/WorksheetUtils";
import {
  isWorksheetMutationsV2Enabled,
  isWorksheetQueriesV2Enabled,
  getIsWorksheetDocEditorEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  QUESTION_TAG_LOCALES,
  WS_AI_QUESTION_TAGS,
  CHOICE_REGEX,
  CORRECT_ANSWER_REGEX,
  WS_ASSISTANT_QUESTIONS_REGEX,
  FIELD_QUESTION_REGEX,
  TYPE_OF_QUESTION_REGEX,
  QUESTION_TEXT_REGEX,
  CHOICE_TAGS_REGEX,
  WORKSHEET_ELEMENT_IDS,
} from "Worksheet/modules/WorksheetConstants";
import { TEMP_ID } from "AppComponents/AIContextBar/modules/Constants";

// Module
import {
  getSelectedCurriculumId,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getUserId,
} from "modules/CommonSelectors";
import { getLocalisedTemplate } from "IBPlanner/modules/IBPlannerModule";
import {
  cancelRequest,
  onSendQuery,
  createAIConversation,
  addQuestionToWorksheet,
  replaceQuestionToWorksheet,
  addAssessmentToolToQuestion,
  updateLocalFields,
  replaceQuestionToWorksheetV2,
} from "./modules/AiQuestionAssistantModule";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  updateAiEnhanceQuestion,
  setAiEnhancePrompts,
} from "Worksheet/modules/WorksheetModules";
import {
  getAssessmentQuestionsEnhancer,
  getAssessmentQuestionsEnhancerV2,
} from "Worksheet/modules/WorksheetEnhancers";

// GraphQL
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { useWorksheetAiConstantPrompts } from "Worksheet/hooks/useWorksheetAiConstantPrompts";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { getAIConversationsQuery } from "../LessonPlanningAssistant/modules/LessonPlanningQueries";
import { getAIConversationsFromCache } from "../LessonPlanningAssistant/modules/LessonPlanningGraphqlHelpers";

const styles = {
  textAreaInput: {
    fontSize: "1.4rem",
    lineHeight: "2.4rem",
    padding: "12px 76px 12px 52px",
    ...fontStyles.medium,
  },
  popoverContainerStyle: {
    zIndex: 1000,
  },
  overlayInnerStyle: {
    maxWidth: "240px",
  },
  containerStyle: {
    padding: "0",
    backgroundColor: colors.white,
  },
  inputContainerStyle: {
    flex: "1",
  },
  inputStyle: {
    outline: "none",
    border: "0px",
    padding: "0px",
    borderRadius: "0px",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    color: colors.textDefault,
    ...fontStyles.medium,
  },
  customTextStyle: {
    fontSize: "14px",
    color: colors.textDefault,
    ...fontStyles.demiBold,
    fontWeight: "500",
    lineHeight: "20px",
  },
  closeArrowIconStyle: {
    color: colors.neutral400,
  },
};

const markdownComponents = {
  hr: () => <p>{"-----"}</p>,
};

function getAlphabetIndexing(number) {
  return String.fromCharCode(96 + number);
}

const { CHOICES_TAG, CORRECT_ANSWER_TAG } = WS_AI_QUESTION_TAGS;
function processXmlTags(input, t) {
  // Match starting tags
  const startingTagsRegex = /<([^\/>]+)>/g;
  // Remove <questionText> tag
  let output = input.replace(QUESTION_TEXT_REGEX, "");

  // Remove empty tags and replace starting tags with "**Tag Name**\n"
  output = output
    .replace(/<([^/>]+)>\s*<\/\1>/g, "")
    .replace(/(\s*\n\s*)+/g, "\n")
    .replace(/--- \([a-zA-Z0-9@]+\)/g, "-");
  if (_.includes(input, `<${CHOICES_TAG}>`)) {
    output = output
      .replace(CHOICE_REGEX, (withTag, choicesContent) => {
        const choices = choicesContent
          .trim()
          .split("\n")
          .map(choice => choice.trim());
        const numberedChoices = _.map(
          choices,
          (choice, index) =>
            `  ${getAlphabetIndexing(index + 1)}) ${_.replace(
              choice,
              "-- ",
              ""
            )}`
        );

        return `\n**${t(
          QUESTION_TAG_LOCALES[CHOICES_TAG]
        )}**\n\n${numberedChoices.join("\n")}`;
      })
      .replace(CORRECT_ANSWER_REGEX, (_, correctAnswerIndex) => {
        const choices = output
          .match(CHOICE_REGEX)[0]
          .replace(CHOICE_TAGS_REGEX, "")
          .trim()
          .split("\n")
          .map(choice => choice.trim().replace("-- ", ""));
        const correctChoice = choices[parseInt(correctAnswerIndex)];
        return `\n**${t(
          QUESTION_TAG_LOCALES[CORRECT_ANSWER_TAG]
        )}**\n\n  ${getAlphabetIndexing(
          parseInt(correctAnswerIndex) + 1
        )}) ${correctChoice}\n`;
      });
  }

  output = output
    .replace(/<\/[^>]+>/g, "")
    .replace(startingTagsRegex, (_, tagName) => {
      // Capitalize the first letter of the first word in the tag name
      const tagData = QUESTION_TAG_LOCALES[tagName]
        ? t(QUESTION_TAG_LOCALES[tagName])
        : "";
      if (!tagData.trim()) return "";
      return `\n**${tagData}**\n\n`;
    });

  return output;
}

function filterQuestionsAndPlainText(inputText) {
  const separator = "###";

  const parts = inputText.split(separator);

  const result = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    const matches = part.match(WS_ASSISTANT_QUESTIONS_REGEX) || [];

    if (matches?.length > 0) {
      const questions = matches.map((match, index) => ({
        text: match
          .replace(FIELD_QUESTION_REGEX, "")
          .replace(TYPE_OF_QUESTION_REGEX, ""),
        type: "question",
        count: index + 1,
      }));

      result.push(...questions);
    } else if (part.length > 0) {
      result.push({ text: part, type: "plaintext", count: "" });
    }
  }

  return result;
}

const AiQuestionAssistant = memo(props => {
  const {
    t,
    setShowAssistantRightPane,
    portalType,
    leId,
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    messages,
    aiConversationId,
    resetAIConversation,
    onSendQuery,
    assessmentType,
    gradeId,
    assessmentDetails,
    isInitialState,
    getCurrentAssessmentTypes,
    course,
    middlePaneRef,
    createAIConversation,
    mode,
    entityType,
    unitPlanId,
    sourceFrom,
    isFromUnitPlan,
    courseId,
    unitPlanDetails,
    unitPlanTemplate: _unitPlanTemplate,
    assessmentTemplate,
    refetchAssessments,
    worksheetMutationData,
    data: worksheetData, //worksheet data from enhancer
    aiEnhanceQuestion,
    updateAiEnhanceQuestion,
    curriculumProgramType,
    setAiEnhancePrompts,
    isQuestionAssistant,
    creationMode,
    isDraft,
    selectedCourse,
    conversationMetadata,
    conversationAttachments,
    question,
    addQuestionToWorksheet,
    replaceQuestionToWorksheet,
    addAssessmentToolToQuestion,
    updateAIConversation,
    learningGoalsFields,
    updateLocalFields,
    updateSettings,
    userId,
    templateId,
    replaceQuestionToWorksheetV2,
  } = props;

  const {
    data: { allPrompts },
  } = useWorksheetAiConstantPrompts({
    assessmentDetails,
    entityType: "QUESTION",
    entityId: _.get(question, "questionId", ""),
  });

  const constantPrompts = useMemo(
    () =>
      isQuestionAssistant
        ? allPrompts
        : _.filter(
            allPrompts,
            prompt => !_.isEqual("ASSESSMENT_TOOL", _.get(prompt, "groupType"))
          ),
    [allPrompts, isQuestionAssistant]
  );

  const showDocEditor = getIsWorksheetDocEditorEnabled();
  const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();
  const currentAssessmentTypes = getCurrentAssessmentTypes();
  const lastUserMessageId = _.get(
    _.findLast(messages, ({ node }) => node?.authorType === "user"),
    "node.id"
  );
  const questionTitle = _.get(question, "questionTitle", "");
  const isBlankQuestion = _.get(question, "isBlankQuestion", false);

  // states

  const [query, setQuery] = useState("");
  const [currentAIState, setCurrentAIState] = useState("");
  const [currentDialogAction, setCurrentDialogueAction] = useState({
    action: "",
    params: {
      isChecked: false,
    },
  });
  const [selectedAction, setSelectedAction] = useState({});
  const [questionId, setQuestionId] = useState(
    _.get(question, "questionId", "")
  );

  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [promptSuggestionModalInfo, setPromptSuggestionModalInfo] = useState({
    showModal: false,
    params: {},
  });
  const [editUserPromptObj, setEditUserPromptObj] = useState({
    isEditMode: false,
    userPrompt: "",
  });
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  const [aiVoiceModalLanguage, setAiVoiceModalLanguage] = useState("en");
  const [context, setContext] = useState({});
  const [excludedFields, setExcludedFields] = useState([]);

  const [isAnalysing, setIsAnalysing] = useState(false);
  const [sendButtonParams, setSendButtonParams] = useState({});

  const containerRef = useRef(null);
  const scrollEndRef = useRef(null);

  const { isEditMode = false, userPrompt } = editUserPromptObj;

  const ALTERNATE_QUESTION_TYPES = getAlternateQuestionTypes(t);

  // whenever the user changes the language in the AI voice modal then
  // set the selected language in local state and load that language bundle
  const setConfigureAIVoiceRef = useCallback(async node => {
    const { preferredLanguageOptions: languageOptions, preferredVoiceObj } =
      node || {};

    const selectedLanguage = _.get(preferredVoiceObj, "language", "en");

    const languageKey = _.find(languageOptions, {
      key: selectedLanguage,
    })?.lng;

    if (languageKey) {
      setAiVoiceModalLanguage(languageKey);
    }
  }, []);

  const resolvedSubjectNodes = _.get(
    _.find(_.get(assessmentDetails, "allFields", []), {
      uid: "subjects",
    }),
    "resolvedMinimalTree.subjects",
    []
  );

  const resolvedSubjectGroupNodes = _.map(resolvedSubjectNodes, "subjectGroup");

  const improvePrompts = getImprovePrompts({
    t,
    constantPrompts,
    language: aiVoiceModalLanguage,
    subjectGroups: resolvedSubjectGroupNodes,
    subjects: resolvedSubjectNodes,
    curriculumProgramType,
  });

  const handleImproveOptionClick = (
    option,
    questionText,
    questionTypeValue
  ) => {
    const key = _.get(option, "key");
    let action = key;

    // * externally we have to set action for convert to alternate question types, as we have handled question types on FE
    if (isConvertToAlternateAction(key, t)) {
      action = CONVERT_QUESTION_FORMAT;
    }

    const prompt = _.find(improvePrompts, { action });
    if (prompt) {
      const newContext = {
        ...context,
        [CF_QUESTION_TEXT]: questionText,
        [CF_TYPE_OF_QUESTION]: questionTypeValue,
      };

      // * adding required context data for convert to alternate action
      if (isConvertToAlternateAction(key, t)) {
        const newQuestionType = _.get(
          _.find(ALTERNATE_QUESTION_TYPES, { key }),
          "value"
        );

        newContext[CF_NEW_QUESTION_TYPE] = newQuestionType;
      }

      setSelectedAction(prompt);
      setContext(newContext);
    }
  };

  // side effects

  // hide intercom because it is coming above chat send button
  useEffect(() => {
    const { showIntercom, hideIntercom } = props;
    hideIntercom();
    return () => {
      showIntercom();
    };
  }, []);

  // on message sent scroll to bottom
  useEffect(() => {
    if (!_.isEmpty(messages)) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom, currentAIState]);

  // on selection selected send it as a message
  useEffect(() => {
    // In case of a blank question, the default action should be selected, so onSendMessage should not be called. It should only be triggered by pressing Enter.
    const isBlankQuestionFlow =
      isBlankQuestion &&
      _.isEqual(_.get(selectedAction, "action"), GENERATE_QUESTIONS);

    if (
      !_.isEmpty(selectedAction) &&
      (!isQuestionAssistant || _.isEmpty(selectedAction?.label)) &&
      !isBlankQuestionFlow
    ) {
      onSendMessage();
    }
  }, [selectedAction]);

  useEffect(() => {
    if (
      _.get(sendButtonParams, "attachmentHasTempId") &&
      _.every(conversationAttachments, ({ id }) => !_.includes(id, TEMP_ID))
    ) {
      onSendMessage();
    }
  }, [conversationAttachments, sendButtonParams?.attachmentHasTempId]);

  useEffect(() => {
    return () => {
      onClickStop();
    };
  }, []);

  useEffect(() => {
    // create a new ai conversation for old assignments (where ai conversation does not exist)
    const shouldCreateConversation =
      mode === "edit" &&
      _.isEmpty(aiConversationId) &&
      !_.isEmpty(assessmentDetails) &&
      !isQuestionAssistant;

    if (shouldCreateConversation) {
      const _createAIConversation = async () => {
        await createAIConversation({
          input: {
            entityType: "ASSESSMENT",
            entityId: leId,
          },
        });
      };

      _createAIConversation();
    }
  }, [aiConversationId, isQuestionAssistant]);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  //useeffect for left side ai enhancement
  useEffect(() => {
    const { action, hasGeneratedQuestion, questionId } =
      aiEnhanceQuestion || {};
    if (!hasGeneratedQuestion) {
      updateAiEnhanceQuestion({
        ...aiEnhanceQuestion,
        hasGeneratedQuestion: true,
      });

      const prompt = _.find(improvePrompts, { action });
      if (prompt) {
        setQuestionId(questionId);
        setSelectedAction(prompt);
      }
    }
  }, [aiEnhanceQuestion]);

  const enhancePrompts = _.filter(constantPrompts, {
    groupType: "ENHANCE_OPTION",
  });

  useEffect(() => {
    setAiEnhancePrompts(enhancePrompts);

    if (isQuestionAssistant) {
      const activeQuestionType = _.get(
        QUESTION_TYPE_UID_VALUE_MAPPING,
        _.get(question, "questionTypeUID")
      );

      setContext(prevContext => ({
        ...prevContext,
        [CF_TYPE_OF_QUESTION]: activeQuestionType,
      }));
    }
  }, []);

  // update default prompt for blank question
  useEffect(() => {
    const updatedPrompts = getUpdatedPrompts({
      t,
      value: constantPrompts,
      subjectGroups: resolvedSubjectGroupNodes,
      subjects: resolvedSubjectNodes,
      isEnglishLanguageSelected: _.isEqual(aiVoiceModalLanguage, "en"),
      curriculumProgramType,
    });

    const defaultAction = _.find(updatedPrompts, {
      action: GENERATE_QUESTIONS,
    });

    if (isQuestionAssistant && isBlankQuestion && !_.isEmpty(defaultAction))
      setSelectedAction(defaultAction);
  }, []);

  // used for update local fields into conversation metadata
  useEffect(() => {
    updateLocalFields({ aiConversationId, context, conversationMetadata });
  }, [context]);

  // update conversation metadata's localFields data in context
  useMount(() => {
    const localFields = _.get(conversationMetadata, "localFields", []);

    const localFieldsContext = _.reduce(
      localFields,
      (acc, { field, value }) => {
        _.set(acc, field, value);
        return acc;
      },
      {}
    );

    setContext(prev => ({ ...prev, ...localFieldsContext }));
  });

  const unitPlanTemplate = _.get(
    getLocalisedTemplate({
      template: { body: _.cloneDeep(_unitPlanTemplate) },
      t,
      templateType: "unitPlan",
    }),
    "body",
    {}
  );

  const fieldList = _.get(assessmentTemplate, "field_list", {});
  const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});

  const stepList = _.get(assessmentTemplate, "step_list", {});
  const unitPlanStepList = _.get(unitPlanTemplate, "step_list", {});

  // functions
  const scrollToBottom = () => {
    if (scrollEndRef.current && shouldScrollToBottom) {
      scrollIntoView({ element: scrollEndRef.current });
    }
  };

  const updatePromptSuggestionModalInfo = params => {
    setPromptSuggestionModalInfo(prevState => {
      return {
        ...prevState,
        ...params,
      };
    });
  };

  const onPromptSelection = prompt => {
    setSelectedAction(prompt);
  };

  const onDialogConfirm = async () => {
    const { action, params } = currentDialogAction || {};

    switch (action) {
      case "CLEAR_CHAT":
        {
          // 1. show full screen loader
          setCurrentAIState(LOADING);

          // 2. reset the AI conversation
          await resetAIConversation({
            input: {
              id: aiConversationId,
              entityId: isQuestionAssistant ? questionId : leId,
              entityType: isQuestionAssistant ? "QUESTION" : ASSESSMENT,
            },
          });

          // 3. update the selected language in the AI conversation and reset promptFields
          const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
            code: aiVoiceModalLanguage,
          });

          const metadata = {
            allFields: [],
            localFields: [],
          };

          if (!_.isEmpty(selectedLanguage)) {
            // 3. update the selected language in the AI conversation and reset promptFields
            const selectedLanguage = _.find(AI_TRANSLATION_LANGUAGES, {
              code: aiVoiceModalLanguage,
            });

            _.set(metadata, "language", _.get(selectedLanguage, "label"));
            _.set(metadata, "languageCode", aiVoiceModalLanguage);
            _.set(
              metadata,
              "selectedDropdownLanguage",
              _.get(selectedLanguage, "id")
            );
          }

          await updateAIConversation({
            input: {
              id: aiConversationId,
              metadata,
            },
          });

          // 4. reset states
          setCurrentAIState("");
          setExcludedFields([]);
        }
        break;

      case "REPLACE_QUESTION":
        {
          if (params?.isChecked) {
            updateSettings({
              settings: [
                {
                  name: WS_AI_SETTING_FLAGS.HIDE_REPLACE_QUESTION_DIALOGUE,
                  value: "true",
                },
              ],
              showToastMsg: false,
            });
          }
          if (isMutationsV2Enabled) {
            replaceQuestionToWorksheetV2(params);
          } else {
            replaceQuestionToWorksheet(params);
          }
        }
        break;

      case "REPLACE_ASSESSMENT_TOOL":
        {
          if (params?.isChecked) {
            updateSettings({
              settings: [
                {
                  name:
                    WS_AI_SETTING_FLAGS.HIDE_REPLACE_ASSESSMENT_TOOL_DIALOGUE,
                  value: "true",
                },
              ],
              showToastMsg: false,
            });
          }

          addAssessmentToolToQuestion(params);
        }
        break;
    }

    setCurrentDialogueAction({ action: "" });
  };

  // send message on enter
  const onKeyDown = e => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const onSendMessage = async data => {
    const isUserMessageEdited = _.get(data, "isUserMessageEdited", false);
    const { checkLoadingState = true } = sendButtonParams;

    if (getIsAIResponseLoading(currentAIState) && checkLoadingState) return;

    const lastMessageId = _.get(_.last(messages), "node.id");
    await onSendQuery({
      t,
      query,
      leId,
      aiConversationId,
      selectedAction,
      setQuery,
      setSelectedAction,
      setCurrentAIState,
      gradeId,
      course,
      lastMessageId,
      isUserMessageEdited,
      editUserPromptObj,
      setEditUserPromptObj,
      messages,
      language: aiVoiceModalLanguage,
      context,
      setContext,
      fieldList,
      unitPlanFieldList,
      unitPlanDetails,
      assessmentDetails,
      excludedFields,
      entityType,
      stepList,
      unitPlanStepList,
      questionId,
      isQuestionAssistant,
      conversationMetadata,
      conversationAttachments,
      learningGoalsFields,
      setIsAnalysing,
      setSendButtonParams,
      sendButtonParams,
    });

    // * omit extra fields from context once action performed
    if (isQuestionAssistant) {
      const toBeRemoved = [CF_NEW_QUESTION_TYPE, CF_QUESTION_TEXT];
      const anyFieldPresent = _.some(toBeRemoved, key => _.has(context, key));

      if (anyFieldPresent) {
        setContext(prevContext => _.omit(prevContext, toBeRemoved));
      }
    }
  };

  const onClickStop = () => {
    cancelRequest?.();
    setCurrentAIState("");
  };

  const onScrollBodyContainer = e => {
    setShouldScrollToBottom(checkScrollAtBottom(e.target, 56)); // ignore paddingBottom of 56px
  };

  const renderPrompts = () => {
    const combinedPrompts = getCombinedPrompts({
      t,
      constantPrompts,
      language: aiVoiceModalLanguage,
      subjectGroups: resolvedSubjectGroupNodes,
      subjects: resolvedSubjectNodes,
      curriculumProgramType,
    });

    const showPrompts = !_.isEmpty(combinedPrompts);

    return showPrompts ? (
      <div className={classes.promptsContainer}>
        {_.map(combinedPrompts, (prompt, index) => {
          const { id, options, label, style, visible = true } = prompt || {};

          if (!visible) return null;

          const { emoji, text } = getEmojisAndTextFromString(label) || {};

          const promptOptions = _.map(options, option => {
            const { id: optionId, label, isNewTagVisible } = option || {};

            const onClickOption = () => {
              if (optionId === "MORE") {
                updatePromptSuggestionModalInfo({
                  showModal: true,
                  params: { groupType: id },
                });
              } else {
                setSelectedAction(option);
              }
            };

            return (
              <div
                key={optionId}
                className={classes.tag}
                style={style}
                onClick={onClickOption}
                data-test-id={"worksheet-worksheetAssistant-selectOption"}
              >
                {label}
                <NewTag t={t} isNewTagVisible={isNewTagVisible} />
              </div>
            );
          });

          const labelClass = classNames(
            "text-label-2",
            classes.textDefault,
            classes.tagContainer
          );

          const groupedTagsClass = classNames(
            classes.flexDirectionColumn,
            classes.fullWidth
          );

          return (
            <div className={groupedTagsClass} key={id}>
              <div className={labelClass}>
                {emoji ? (
                  <div
                    className={classes.emojiContainer}
                    style={{
                      backgroundColor:
                        EMOJI_COLOR_TRAY[index % _.size(combinedPrompts)],
                    }}
                  >
                    {emoji}
                  </div>
                ) : null}
                <div className={classes.flexDirectionColumn}>
                  <div className={classes.promptGroupLabel}>{text}</div>
                  <div className={classes.flexWrap}>{promptOptions}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : null;
  };

  const renderEmptyState = () => {
    return <div className={classes.emptyState}>{renderPrompts()}</div>;
  };

  const getMessageLoader = ({ message, isInline = false }) => {
    const messageLoaderClasses = classNames({
      [classes.generatingMsg]: true,
      [classes.inlineMessageLoader]: isInline,
    });

    const messageClasses = classNames({
      [classes.generatingSingleMsg]: true,
      [classes.inlineLoaderMsg]: isInline,
    });

    const messageLoader = (
      <div className={messageLoaderClasses}>
        <div className={messageClasses}>{message}</div>
        <div className={classes.dot} />
      </div>
    );

    if (isInline) {
      return messageLoader;
    }

    return (
      <div className={classes.assistantMsg}>
        <div className={classes.generatingContent}>
          <Shifu3D width={40} height={40} className={classes.assistantAvatar} />
          <div className={classes.generatingMsgContainer}>{messageLoader}</div>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    return (
      <React.Fragment>
        {_.map(messages, ({ node }, index) => {
          const {
            id,
            authorType,
            content,
            metadata,
            createdBy,
            votes,
            usageMetricsId,
          } = node || {};

          const { firstName, lastName, profileImage } = createdBy || {};
          const { language } = metadata || {};

          const isLastUserMessage = lastUserMessageId === id;
          const showButton = isEditMode || hoveredMessageId === id;
          const showEditMessageIcon =
            checkIsLEAIUserMessageEditEnabled() &&
            isLastUserMessage &&
            metadata.isUserInputMessage &&
            showButton;

          const assessmentToolType = getToolTypeFromContent({
            language: language || aiVoiceModalLanguage,
            content,
          });

          switch (authorType) {
            case "user": {
              return (
                <div
                  key={id}
                  className={classes.messageContainer}
                  onMouseEnter={() => setHoveredMessageId(id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  <div className={classes.userMsg}>
                    <Avatar
                      size={"medium"}
                      shape={"square"}
                      shade={"light"}
                      className={classes.noShrink}
                      name={`${firstName} ${lastName}`}
                      src={profileImage}
                    />
                    {isEditMode && isLastUserMessage ? (
                      <InputTextField
                        value={userPrompt}
                        name={"userPrompt"}
                        inputStyle={styles.inputStyle}
                        containerStyle={styles.inputContainerStyle}
                        updateInputField={params =>
                          setEditUserPromptObj({
                            ...editUserPromptObj,
                            ...params,
                          })
                        }
                      />
                    ) : (
                      <div className={classes.singleMsg}>{content}</div>
                    )}
                  </div>
                  {showEditMessageIcon && (
                    <div className={classes.buttonContainer}>
                      {isEditMode && isLastUserMessage ? (
                        <>
                          <IconButton
                            icon={<CloseOutlined />}
                            variant="plain"
                            size="small"
                            onClick={() =>
                              setEditUserPromptObj({
                                isEditMode: false,
                                userPrompt: "",
                              })
                            }
                            testId={
                              "worksheet-worksheetAssistant-closeEditPrompt"
                            }
                          />
                          <IconButton
                            icon={<TickLargeOutlined />}
                            variant="progressive"
                            size="small"
                            onClick={() => {
                              onSendMessage({ isUserMessageEdited: true });
                              setEditUserPromptObj({
                                ...editUserPromptObj,
                                isEditMode: false,
                              });
                            }}
                            testId={
                              "worksheet-worksheetAssistant-sendUserPrompt"
                            }
                          />
                        </>
                      ) : (
                        <IconButton
                          icon={<PencilLineOutlined />}
                          variant="plain"
                          size="x-small"
                          onClick={() =>
                            setEditUserPromptObj({
                              isEditMode: true,
                              userPrompt: content,
                              lastUserMessageId,
                            })
                          }
                          testId={"worksheet-worksheetAssistant-editUserPrompt"}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            }

            case "assistant": {
              let blocks = null;

              const {
                userInputFlagged,
                model,
                temperature,
                intentUsageMetricsId,
              } = metadata || {};

              const blockContentClass = classNames({
                [classes.blockContentText]: true,
                [classes.boldMarker]: getIsListTitleBold(content),
              });

              const dividedChunks = filterQuestionsAndPlainText(content);
              blocks = _.map(dividedChunks, chunk => {
                const { text, type, count } = chunk || {};

                let updatedText = text;
                if (isFillInTheBlankRes(updatedText)) {
                  updatedText = formatAiFillResponse(updatedText);
                }
                updatedText = processXmlTags(updatedText, t);
                updatedText = _.replace(updatedText, /\\\\/g, "\\");

                return {
                  component: (
                    <MarkdownView
                      content={updatedText}
                      contentContainerClass={blockContentClass}
                      components={markdownComponents}
                    />
                  ),
                  type,
                  text,
                  count,
                };
              });

              const onClickAction = ({ key, messageId, blockIndex }) => {
                const { parentId, parentType } = worksheetMutationData;
                const sectionId = _.get(question, "sectionId");
                const parentItemId = _.get(question, "parentItemId", null); //section's item id

                const firstQuestionIndexInTheMessage = _.findIndex(blocks, {
                  type: "question",
                });

                const selectedSection = _.find(worksheetData, section =>
                  isQueriesV2Enabled
                    ? _.isEqual(_.get(section, "item.id"), parentItemId)
                    : _.isEqual(_.get(section, "id"), sectionId)
                );

                const lastQuestion = isQueriesV2Enabled
                  ? _.last(_.get(selectedSection, "children"))
                  : _.last(_.get(selectedSection, "childrenV2.edges"));

                const aiSequence = [
                  blockIndex - firstQuestionIndexInTheMessage + 1,
                ];
                const questionResourceId = _.get(
                  question,
                  "questionResourceId",
                  null
                );

                const putAfterItemId = _.get(question, "putAfterItemId", null);
                const putAfterQuestionId = _.get(
                  question,
                  "putAfterQuestionId",
                  null
                );

                if (_.isEqual(key, "add") && !_.isEmpty(selectedSection)) {
                  addQuestionToWorksheet({
                    parentId,
                    parentType,
                    lastSectionId: _.get(selectedSection, "item.id", null),
                    parentResourceId: _.get(selectedSection, "id", null),
                    putAfterItemId: _.get(lastQuestion, "node.id", null),
                    putAfterItemIdV2: _.get(lastQuestion, "item.id", null),
                    aiMessageId: messageId,
                    aiSequence,
                    hasSection: !_.isEmpty(selectedSection),
                    gradeId,
                    showLoader: true,
                  });

                  return;
                } else if (_.isEqual("replace", key)) {
                  const questionParams = {
                    questionIdToReplace: questionId,
                    parentId,
                    parentType,
                    parentResourceId: sectionId,
                    putAfterItemId,
                    aiMessageId: messageId,
                    aiSequence,
                    questionResourceId,
                    gradeId,
                  };

                  const questionParamsV2 = {
                    questionIdToReplace: questionId,
                    parentItemId,
                    putAfterQuestionId,
                    aiMessageId: messageId,
                    aiSequence,
                    gradeId,
                  };

                  // check if previously user had selected "do not show this message again" checkbox
                  // if yes then do not show the dialog
                  const hasUserReplaceDialogHide = getSettingValue({
                    name: WS_AI_SETTING_FLAGS.HIDE_REPLACE_QUESTION_DIALOGUE,
                    readFromUserAndOrgLevel: true,
                  });

                  if (!hasUserReplaceDialogHide) {
                    setCurrentDialogueAction({
                      action: "REPLACE_QUESTION",
                      params: isMutationsV2Enabled
                        ? questionParamsV2
                        : questionParams,
                    });
                  } else {
                    if (isMutationsV2Enabled) {
                      replaceQuestionToWorksheetV2(questionParamsV2);
                    } else {
                      replaceQuestionToWorksheet(questionParams);
                    }
                  }

                  return;
                } else if (_.isEqual("add_assessment_tool", key)) {
                  const isToolExist = isAssessmentToolsAlreadyExist({
                    questionId,
                    assessmentToolType,
                  });

                  // check if previously user had selected "do not show this message again" checkbox
                  // if yes then do not show the dialog
                  const hasUserReplaceDialogHide = getSettingValue({
                    name:
                      WS_AI_SETTING_FLAGS.HIDE_REPLACE_ASSESSMENT_TOOL_DIALOGUE,
                    readFromUserAndOrgLevel: true,
                  });

                  const toolsParams = {
                    assessmentToolType,
                    content,
                    questionId,
                    portalType,
                    assessmentId: _.get(assessmentDetails, "id"),
                  };

                  if (isToolExist && !hasUserReplaceDialogHide) {
                    setCurrentDialogueAction({
                      action: "REPLACE_ASSESSMENT_TOOL",
                      params: toolsParams,
                    });
                  } else {
                    addAssessmentToolToQuestion(toolsParams);
                  }

                  return;
                }
              };

              const getActions = ({ questionText, messageId, blockIndex }) => {
                const actionsList = getBlockActions({
                  isQuestionAssistant,
                  assessmentToolType,
                  t,
                });

                const questionTypeValue = getTypeOfQuestionFromContent(content);

                const onEnhanceItemClick = option => {
                  if (!_.isEmpty(query)) {
                    setQuery("");
                  }

                  handleImproveOptionClick(
                    option,
                    questionText,
                    questionTypeValue
                  );
                };

                const isActionsDisabled = !_.isEmpty(currentAIState);

                return (
                  <div className={classes.blockActions}>
                    {_.map(actionsList, action => {
                      const {
                        key,
                        label,
                        icon,
                        disabled = false,
                        disabledTooltip,
                        isLocalised = false,
                        tooltipProps = {},
                        localisationParams,
                      } = action || {};

                      return (
                        <ConditionalWrapper
                          key={key}
                          type={"wrapperAsElement"}
                          condition={disabled && !_.isEmpty(disabledTooltip)}
                          wrapper={
                            <Tooltip
                              placement={"bottom"}
                              tooltip={
                                isLocalised
                                  ? disabledTooltip
                                  : localisationParams
                                  ? t(disabledTooltip, localisationParams)
                                  : t(disabledTooltip)
                              }
                              overlayInnerStyle={styles.overlayInnerStyle}
                              {...tooltipProps}
                            />
                          }
                        >
                          {key === "enhance" ? (
                            <Dropdown
                              overlay={
                                <DropdownMenu
                                  options={getEnhanceOptions({
                                    enhancePrompts,
                                    questionTypeValue,
                                    t,
                                  })}
                                  onClick={onEnhanceItemClick}
                                  testId={
                                    "worksheet-worksheetAssistant-handleImproveOptionClick"
                                  }
                                />
                              }
                              disabled={isActionsDisabled}
                            >
                              <ButtonDisclosure
                                variant={"plain"}
                                icon={icon}
                                isCompact={true}
                                size={"small"}
                                disabled={isActionsDisabled}
                                testId={
                                  "worksheet-worksheetAssistant-handleImproveOptionClick"
                                }
                                className={classes.actionBtn}
                                aria-label={t("classRoom:improve")}
                              />
                            </Dropdown>
                          ) : key === "add_or_replace" ? (
                            <Dropdown
                              overlay={
                                <DropdownMenu
                                  options={getQuestionActionOptions(t)}
                                  onClick={option =>
                                    onClickAction({
                                      key: _.get(option, "key"),
                                      messageId,
                                      blockIndex,
                                    })
                                  }
                                  testId={
                                    "worksheet-worksheetAssistant-onClickAction"
                                  }
                                />
                              }
                              disabled={isActionsDisabled}
                            >
                              <ButtonDisclosure
                                variant={"plain"}
                                icon={icon}
                                isCompact={true}
                                size={"small"}
                                className={classes.actionBtn}
                                disabled={isActionsDisabled}
                                testId={
                                  "worksheet-worksheetAssistant-addReplaceAction"
                                }
                              >
                                {t(label)}
                              </ButtonDisclosure>
                            </Dropdown>
                          ) : (
                            <Button
                              key={key}
                              size={"small"}
                              variant={"inline"}
                              icon={icon}
                              onClick={() =>
                                onClickAction({
                                  key,
                                  messageId,
                                  blockIndex,
                                })
                              }
                              disabled={disabled || isActionsDisabled}
                              testId={
                                "worksheet-worksheetAssistant-openDropdown"
                              }
                            >
                              {localisationParams
                                ? t(label, localisationParams)
                                : t(label)}
                            </Button>
                          )}
                        </ConditionalWrapper>
                      );
                    })}
                  </div>
                );
              };

              const isLastMessage = _.size(messages) - 1 === index;
              const showActions =
                !userInputFlagged &&
                (!isLastMessage ||
                  (isLastMessage && !getIsAIResponseLoading(currentAIState)));

              const blockClass = classNames({
                [classes.block]: true,
                [classes.textCritical]: userInputFlagged,
              });

              const blockWrapperClass = classNames({
                [classes.blockWrapper]: true,
                [classes.violationBlock]: userInputFlagged,
              });

              if (_.isEmpty(blocks) && isAnalysing) {
                return getMessageLoader({
                  message: t("worksheet:analysing"),
                });
              }

              return _.size(blocks) ? (
                <div key={id} className={classes.assistantMsg}>
                  <div className={classes.assistantMsgUpper}>
                    <Shifu3D
                      width={40}
                      height={40}
                      className={classes.assistantAvatar}
                    />
                  </div>
                  <div className={classes.blockWrapperParentContainer}>
                    {_.map(
                      blocks,
                      ({ component, type, text, count }, blockIndex) => {
                        const isQuestionChunk = _.isEqual(type, "question");

                        const showAnalysingLoader =
                          isQuestionChunk && isAnalysing && isLastMessage;

                        const isAssessmentTool = _.has(
                          ASSESSMENT_TOOLS_OBJ,
                          assessmentToolType
                        );

                        return (
                          <div className={blockWrapperClass} key={blockIndex}>
                            <div className={blockClass}>
                              {isQuestionChunk ? (
                                <div>
                                  <strong>
                                    {t(
                                      "worksheet:question_abbreviation_with_number",
                                      {
                                        number: count,
                                      }
                                    )}
                                  </strong>
                                </div>
                              ) : null}

                              <div>
                                {component}
                                {showAnalysingLoader
                                  ? getMessageLoader({
                                      message: t("worksheet:analysing"),
                                      isInline: true,
                                    })
                                  : null}
                              </div>
                            </div>
                            {showActions &&
                            (!_.isEmpty(type) || isAssessmentTool) ? (
                              <div className={classes.actionsContainer}>
                                {_.isEqual(type, "question") ||
                                isAssessmentTool ? (
                                  getActions({
                                    questionText: text,
                                    messageId: id,
                                    authorType,
                                    blockIndex,
                                  })
                                ) : (
                                  <p></p>
                                )}
                                <AIFeedbackStrip
                                  showWarningText={false}
                                  containerStyle={styles.containerStyle}
                                  defaultFeedbackStatus={votes.myVote}
                                  usageMetricsId={usageMetricsId}
                                  model={model}
                                  temperature={temperature}
                                  intentUsageMetricsId={intentUsageMetricsId}
                                  sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                                  showLikeButton={false}
                                  showDislikeButton={false}
                                />
                              </div>
                            ) : null}
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              ) : null;
            }
            case "prerequisite": {
              const isLastMessage = _.size(messages) - 1 === index;
              const isLastMessagePrerequisite =
                isLastMessage && !getIsAIResponseLoading(currentAIState);

              if (!isLastMessagePrerequisite) return null;

              return (
                <div className={classes.assistantMsg}>
                  <PrerequisiteSelection
                    t={t}
                    prerequisiteFieldsData={metadata || {}}
                    portalType={portalType}
                    fieldList={fieldList}
                    stepList={stepList}
                    assessmentDetails={assessmentDetails}
                    unitPlanId={unitPlanId}
                    context={context}
                    setContext={setContext}
                    mode={mode}
                    middlePaneRef={middlePaneRef}
                    isFromUnitPlan={isFromUnitPlan}
                    sourceFrom={sourceFrom}
                    setSelectedAction={setSelectedAction}
                    messages={messages}
                    courseId={courseId}
                    unitPlanTemplate={unitPlanTemplate}
                    unitPlanDetails={unitPlanDetails}
                    refetchAssessments={refetchAssessments}
                    course={course}
                    excludedFields={excludedFields}
                    setExcludedFields={setExcludedFields}
                    isQuestionAssistant={isQuestionAssistant}
                    conversationMetadata={conversationMetadata}
                    aiConversationId={aiConversationId}
                  />
                </div>
              );
            }
          }
        })}

        {currentAIState === WRITING
          ? getMessageLoader({ message: t("classRoom:writing") })
          : null}
      </React.Fragment>
    );
  };

  const renderHeader = () => {
    if (!isQuestionAssistant && isInitialState) {
      const titleClass = classNames(
        "heading-5",
        classes.textDefault,
        classes.titleContainer
      );

      const subTitleClass = classNames("text-body-2", classes.subtitle);

      const { label, localeLabel, pluralLocaleLabel } =
        currentAssessmentTypes?.[assessmentType] || {};

      const _label = pluralLocaleLabel || localeLabel;
      const assessmentTypeLabel = _label ? t(_label) : label;

      return (
        <header className={classes.headerEmptyState}>
          <div className={titleClass}>
            <Shifu3D
              width={64}
              height={64}
              className={classes.circleAssistantAvatar}
            />
            {t("classRoom:toddle_ai_empty_state_greet_text")}
          </div>

          <div className={subTitleClass}>
            {t("classRoom:toddle_ai_empty_state_greet_subtext_with_label", {
              label: assessmentTypeLabel,
            })}
          </div>

          <IconButton
            size={"medium"}
            variant={"plain"}
            icon={<CloseOutlined />}
            className={classes.closeButton}
            onClick={() => setShowAssistantRightPane(false)}
            testId={"worksheet-worksheetAssistant-showAssistantRightPane"}
          />
        </header>
      );
    } else {
      const headerClasses = classNames(classes.header, {
        [classes.whiteBackground]: isQuestionAssistant,
      });

      const questionIndex = _.get(question, "questionIndex", "");

      return (
        <header className={headerClasses}>
          <div className={classes.flexWithGap}>
            <Shifu3D
              width={40}
              height={40}
              className={classes.assistantAvatar}
            />
            <div className={"text-label-2"}>
              {t("worksheet:ai_assistant_question", { index: questionIndex })}
            </div>
          </div>
          <div className={classes.flexWithGap}>
            <Tooltip
              tooltip={t("common:clear_chat_history")}
              placement={"bottom"}
            >
              <IconButton
                size={"medium"}
                variant={"plain"}
                disabled={
                  getIsAIResponseLoading(currentAIState) || _.isEmpty(messages)
                }
                icon={<RefreshArrowOutlined />}
                onClick={() => {
                  setCurrentDialogueAction({
                    action: "CLEAR_CHAT",
                    params: {},
                  });
                }}
                testId={"worksheet-worksheetAssistant-clearChat"}
              />
            </Tooltip>

            <IconButton
              size={"medium"}
              variant={"plain"}
              icon={<CloseOutlined />}
              onClick={() => setShowAssistantRightPane(false)}
              testId={"worksheet-worksheetAssistant-closeAssistantRightPane"}
            />
          </div>
        </header>
      );
    }
  };

  const renderBodyContainerEnd = () => {
    if (getIsAIResponseLoading(currentAIState)) {
      return <div className={classes.scrollEnd} ref={scrollEndRef} />;
    } else {
      const showDisclaimerText =
        !isInitialState &&
        _.some(messages, ({ node }) => node?.authorType === "assistant");

      if (showDisclaimerText) {
        const disclaimerTextClass = classNames({
          [classes.disclaimerText]: true,
          [classes.margin24]: _.last(messages)?.node?.authorType === "user",
        });

        return (
          <div className={disclaimerTextClass} ref={scrollEndRef}>
            <InformationOutlined variant={"subtle"} size={"xxx-small"} />
            {t("common:ai_responses_disclaimer_msg")}
          </div>
        );
      }
    }

    return null;
  };

  const renderBlankQuestionMessage = () => {
    return (
      <div className={classes.assistantMsg}>
        <div className={classes.assistantMsgUpper}>
          <Shifu3D width={40} height={40} className={classes.assistantAvatar} />
        </div>
        <div className={classes.blockWrapperParentContainer}>
          <div className={classes.blockWrapper}>
            <div className={classes.block}>
              {t("worksheet:generate_question_fill_details_below")}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderQuestionPrompts = () => {
    const questionTypeUID = _.get(question, "questionTypeUID");

    const promptOptions = getEnhancePromptsOptions({
      t,
      constantPrompts,
      language: aiVoiceModalLanguage,
      subjectGroups: resolvedSubjectGroupNodes,
      subjects: resolvedSubjectNodes,
      curriculumProgramType,
      questionTypeUID,
    });

    const questionIndex = _.get(question, "questionIndex", "");

    const handleQuestionPromptsClick = prompt => {
      let selectedPrompt = prompt;
      const selectedPromptKey = _.get(prompt, "key", "");

      if (!_.isEmpty(query)) {
        setQuery("");
      }

      if (isConvertToAlternateAction(selectedPromptKey, t)) {
        const newQuestionType = _.get(
          _.find(ALTERNATE_QUESTION_TYPES, {
            key: selectedPromptKey,
          }),
          "value"
        );

        setContext({
          ...context,
          [CF_NEW_QUESTION_TYPE]: newQuestionType,
          [CF_TYPE_OF_QUESTION]: _.get(
            QUESTION_TYPE_UID_VALUE_MAPPING,
            questionTypeUID
          ),
        });

        selectedPrompt = _.find(promptOptions, {
          action: CONVERT_QUESTION_FORMAT,
        });
      }

      setSelectedAction(selectedPrompt);
    };

    return (
      <div className={classes.questionPromptsContainer}>
        <div className={classes.questionTitleContainer}>
          <div className="text-label">
            {t("worksheet:question_abbreviation_with_number", {
              number: questionIndex,
            })}
          </div>
          <QuestionFieldComponent
            type={"RichText_Ported"}
            fieldValue={getStyleStrippedText({
              text: questionTitle,
              type: "DOCUMENT_EDITOR",
            })}
            fieldMode={"view"}
            portalType={portalType}
            customTextStyle={styles.customTextStyle}
            textStyle={styles.customTextStyle}
            applyDefaultClass={!showDocEditor}
          />
        </div>

        <PromptAction
          promptItems={promptOptions}
          onPromptActionClick={handleQuestionPromptsClick}
          isActionsDisabled={!_.isEmpty(currentAIState)}
        />
      </div>
    );
  };

  const onClickFooterOption = id => {
    switch (id) {
      case "suggestion_library": {
        updatePromptSuggestionModalInfo({
          showModal: true,
          params: {},
        });
      }
    }
  };

  const onClickDoNotShowCheckbox = () => {
    const { isChecked } = dialogParams || {};
    setCurrentDialogueAction({
      action: dialogAction,
      params: {
        ...dialogParams,
        isChecked: !isChecked,
      },
    });
  };

  const stopGeneratingBtnClasses = classNames(classes.stopGeneratingBtn, {
    [classes.revampStopGeneratingBtn]: isQuestionAssistant,
  });

  const footerTopComp =
    getIsAIResponseLoading(currentAIState) &&
    currentDialogAction?.action !== "CLEAR_CHAT" ? (
      <Button
        className={stopGeneratingBtnClasses}
        size={"medium"}
        variant={"outlined-subtle"}
        onClick={onClickStop}
        style={elevations["elevation-2B"]}
      >
        {t("common:stop_with_label", {
          label: t("classRoom:generating"),
        })}
      </Button>
    ) : null;

  const isSendButtonDisabled = isQuestionAssistant
    ? getIsAIResponseLoading(currentAIState) ||
      (!isBlankQuestion && _.isEmpty(selectedAction) && _.isEmpty(query))
    : getIsAIResponseLoading(currentAIState) || _.isEmpty(query);

  const { action: dialogAction, params: dialogParams = {} } =
    currentDialogAction || {};

  // classes

  const bodyContainerClass = classNames({
    [classes.bodyContainer]: true,
    [classes.bodyContainerWithMessages]: !isInitialState,
    [classes.emptyStateBodyContainer]: isInitialState && !isQuestionAssistant,
  });

  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.innerContainerWithMessages]: !isInitialState,
  });

  const footerClass = classNames({
    [classes.footer]: true,
    [classes.footerEmptyState]: isInitialState,
  });

  const containerClasses = classNames(classes.container, {
    [classes.containerRevamp]: isQuestionAssistant,
  });

  return (
    <div
      className={containerClasses}
      ref={containerRef}
      id={WORKSHEET_ELEMENT_IDS.WORKSHEET_QUESTION_ASSISTANT_ID}
    >
      <div className={innerContainerClass}>
        {renderHeader()}

        {isQuestionAssistant ? (
          <div
            className={classes.closeModalArrowBtn}
            onClick={() => setShowAssistantRightPane(false)}
          >
            <RightArrowOutlined style={styles.closeArrowIconStyle} />
          </div>
        ) : null}

        <div className={bodyContainerClass} onScroll={onScrollBodyContainer}>
          {isQuestionAssistant ? (
            <>
              {isBlankQuestion
                ? renderBlankQuestionMessage()
                : renderQuestionPrompts()}

              {!_.isEmpty(messages) ? renderMessages() : null}
            </>
          ) : !_.isEmpty(messages) ? (
            renderMessages()
          ) : (
            renderEmptyState()
          )}

          {renderBodyContainerEnd()}
        </div>
      </div>

      {isQuestionAssistant ? (
        <AIContextBar
          selectedAction={selectedAction}
          setSelectedAction={setSelectedAction}
          value={query}
          onChange={setQuery}
          isInitialState={isInitialState}
          onSendMessage={onSendMessage}
          isSendButtonDisabled={isSendButtonDisabled}
          footerTopComp={footerTopComp}
          context={context}
          setContext={setContext}
          excludedFields={excludedFields}
          setExcludedFields={setExcludedFields}
          creationMode={creationMode}
          sourceFrom={sourceFrom}
          mode={mode}
          isFromUnitPlan={isFromUnitPlan}
          middlePaneRef={middlePaneRef}
          idDraft={isDraft}
          portalType={portalType}
          onClickFooterOption={onClickFooterOption}
          isWritingMessage={getIsAIResponseLoading(currentAIState)}
          setConfigureAIVoiceRef={setConfigureAIVoiceRef}
          courseId={courseId}
          selectedCourse={selectedCourse}
          leId={leId}
          conversationMetadata={conversationMetadata}
          conversationAttachments={conversationAttachments}
          isQuestionAssistant={isQuestionAssistant}
          question={question}
          aiConversationId={aiConversationId}
          chatInputPlaceholder={
            !_.isEmpty(selectedAction)
              ? t("worksheet:type_additional_instructions_here")
              : t("worksheet:chat_here_with_toddle_ai")
          }
          templateId={templateId}
        />
      ) : (
        <footer className={footerClass}>
          {footerTopComp}

          <div className={classes.bottomFooter}>
            <div className={classes.textAreaInputContainer}>
              <div
                className={classes.thunderIconButton}
                onClick={() => {
                  updatePromptSuggestionModalInfo({
                    showModal: true,
                    params: {},
                  });
                }}
                data-test-id={
                  "worksheet-worksheetAssistant-updatePromptSuggestionModalInfo"
                }
              >
                <OpenBookStarOutlined variant="link" />
              </div>
              <TextAreaInput
                value={query}
                size={"large"}
                placeholder={t("classRoom:start_typing_to_get_suggestions")}
                onChange={e => setQuery(e.target.value)}
                style={styles.textAreaInput}
                minRows={1}
                maxRows={5}
                onKeyDown={onKeyDown}
              />

              <div className={classes.footerRightButtons}>
                <ConfigureAIVoice
                  gradeId={gradeId}
                  entityType="ASSESSMENT"
                  headerSubtitle={t("classRoom:ai_voice_modal_subtext")}
                  size={"small"}
                  promptSetEntityType={entityType}
                  customRef={setConfigureAIVoiceRef}
                  isDisabled={getIsAIResponseLoading(currentAIState)}
                />

                <IconButton
                  size={"small"}
                  variant={"progressive"}
                  icon={<SendFilled variant={"on"} />}
                  onClick={onSendMessage}
                  disabled={isSendButtonDisabled}
                  testId={"worksheet-worksheetAssistant-sendMessage"}
                />
              </div>
            </div>
          </div>
        </footer>
      )}

      <Modal
        isOpen={!_.isEmpty(dialogAction)}
        onClose={() => setCurrentDialogueAction({ action: "" })}
        width={"440px"}
        height={"fit-content"}
      >
        <Modal.Header
          title={DIALOGS_INFO?.[dialogAction]?.title({
            t,
            ...dialogParams,
          })}
          isClosable={true}
        />

        <Modal.Body>
          <div className={classes.modalBody}>
            {DIALOGS_INFO?.[dialogAction]?.message({
              t,
              ...dialogParams,
            })}

            {DIALOGS_INFO?.[dialogAction]?.isDoNotShowAgainPopupVisible ? (
              <div
                className={classes.modalCheckboxContainer}
                onClick={onClickDoNotShowCheckbox}
              >
                <Checkbox isChecked={dialogParams?.isChecked} />
                <span>{t("common:dont_show_this_message_again")}</span>
              </div>
            ) : null}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <footer className={classes.modalFooter}>
            <Button
              size={"large"}
              variant={"outlined-subtle"}
              onClick={() => setCurrentDialogueAction({ action: "" })}
              testId={"worksheet-worksheetAssistant-setCurrentDialogueAction"}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonOneText({
                t,
                ...dialogParams,
              })}
            </Button>

            <Button
              size={"large"}
              variant={"destructive"}
              onClick={onDialogConfirm}
              testId={"worksheet-worksheetAssistant-confirm"}
            >
              {DIALOGS_INFO?.[dialogAction]?.buttonTwoText({
                t,
                ...dialogParams,
              })}
            </Button>
          </footer>
        </Modal.Footer>
      </Modal>

      {promptSuggestionModalInfo?.showModal ? (
        <PromptSuggestionModal
          t={t}
          initialGroupType={_.get(
            promptSuggestionModalInfo,
            "params.groupType"
          )}
          onPromptSuggestionModalClose={() =>
            updatePromptSuggestionModalInfo({ showModal: false })
          }
          onPromptSelection={onPromptSelection}
          constantPrompts={constantPrompts}
          language={aiVoiceModalLanguage}
          isAIResponseLoading={getIsAIResponseLoading(currentAIState)}
          resolvedSubjectGroupNodes={resolvedSubjectGroupNodes}
        />
      ) : null}

      {currentAIState === LOADING ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </div>
  );
});

AiQuestionAssistant.displayName = "AiQuestionAssistant";

AiQuestionAssistant.propTypes = {
  isQuestionAssistant: PropTypes.bool,
  questionId: PropTypes.string,
};

AiQuestionAssistant.defaultProps = {
  portalType: "PLANNER",
  isQuestionAssistant: false,
  questionId: "",
};

const mapStateToProps = (state, ownProps) => {
  const academicYearId = getSelectedAcademicYearId(state);
  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const userId = getUserId(state);

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const courseGradeFilters = {
    academicYearId,
  };

  const parentId = _.get(state, "worksheet.parentEntity.id", null);
  const parentType = _.get(state, "worksheet.parentEntity.type", null);

  const worksheetMutationData = {
    worksheet: _.get(state, "worksheet", {}),
    parentId,
    parentType,
  };

  const assessmentQuestionsQueryVariables = getAssessmentQuestionsQueryVariables(
    {
      parentId,
      parentType,
    }
  );

  const assessmentQuestionsQueryVariablesV2 = {
    id: parentId,
    type: parentType,
  };

  const aiEnhanceQuestion = _.get(state, "worksheet.aiEnhanceQuestion", {});

  const isQuestionAssistant = _.get(ownProps, "isQuestionAssistant");
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    curriculumType,
    curriculumProgramId,
    containerStyle: getWorksheetAssistantContainerStyle(isQuestionAssistant),
    courseId,
    isData: true,
    isLoading: false,
    showLoaderBar: false,
    courseGradeFilters,
    userId,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    worksheetMutationData,
    assessmentQuestionsQueryVariables,
    parentId,
    parentType,
    aiEnhanceQuestion,
    collaboratorsFilter,
    assessmentQuestionsQueryVariablesV2,
  };
};

const mapActionCreators = {
  showIntercom,
  hideIntercom,
  resetAIConversation,
  onSendQuery,
  updateSettings,
  getCurrentAssessmentTypes,
  createAIConversation,
  updateAiEnhanceQuestion,
  setAiEnhancePrompts,
  addQuestionToWorksheet,
  replaceQuestionToWorksheet,
  addAssessmentToolToQuestion,
  updateAIConversation,
  updateLocalFields,
  replaceQuestionToWorksheetV2,
};

export default compose(
  I18nHOC({ resource: ["ai", "common", "worksheet"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentQuery, {
    name: "getAssessment",
    options: ({ leId, portalType, collaboratorsFilter }) => ({
      // cache-first is used because this query will always get called in AssessmentDetails.js and latest data will be available in cache
      // if we use cache-and-network, it will make a network call and override the cache data
      // the reason to not use getAssessmentDetailsFromCache directly is this component won't get re-rendered if cache data changes
      fetchPolicy: "cache-first",
      variables: { id: leId, portalType, collaboratorsFilter },
    }),
    props({
      getAssessment,
      ownProps: { leId, isQuestionAssistant, question, collaboratorsFilter },
    }) {
      const assessmentDetails = getAssessmentDetailsFromCache({
        id: leId,
        collaboratorsFilter,
      });

      let aiConversationId = getAiConversationId(
        _.get(assessmentDetails, "aiConversation", [])
      );

      if (isQuestionAssistant) {
        aiConversationId = _.get(question, "questionAIConversationId");
      }

      return {
        assessmentDetails,
        aiConversationId,
        entityType: "WORKSHEET",
        refetchAssessments: getAssessment.refetch,
      };
    },
  }),
  graphql(getAIConversationsQuery, {
    name: "getAIConversations",
    skip: ({ aiConversationId }) => _.isEmpty(aiConversationId),
    options: ({ aiConversationId, portalType }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: aiConversationId, portalType },
      context: {
        useHttpLink: true,
      },
    }),
    props({
      getAIConversations,
      ownProps: {
        isData,
        isLoading,
        t,
        leId,
        mode,
        sourceFrom,
        curriculumType,
        courseId,
        selectedCourse,
        courseGradeFilters,
        plannerElementSets,
        i18n,
        isQuestionAssistant,
        collaboratorsFilter,
      },
    }) {
      const queryData = getAIConversationsFromCache(
        getAIConversations.variables
      );

      const networkStatus = _.get(getAIConversations, "networkStatus");

      const messages = [..._.get(queryData, "messages.edges", [])].reverse();

      const pageInfo = _.get(queryData, "messages.pageInfo", {});

      const hasNextPage = _.get(pageInfo, "hasNextPage", false);
      const endCursor = _.get(pageInfo, "endCursor");

      const conversationMetadata = _.get(queryData, "metadata");
      const conversationAttachments = _.get(queryData, "attachments", []);
      let assessmentDetails = getAssessmentDetailsFromCache({
        id: leId,
        collaboratorsFilter,
      });

      const course = !_.isEmpty(selectedCourse)
        ? selectedCourse
        : getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          });

      const gradeId =
        _.get(course, "primaryGrade.id") || _.get(course?.grades, "0.id");

      const assessmentType = _.get(assessmentDetails, "assessmentType.value");
      const groupType = _.get(assessmentDetails, "type");
      const templateId = _.get(assessmentDetails, "templateId", null);

      const unitPlan = getUnitPlanFromAssessment({ assessmentDetails });

      const unitPlanTemplateId = _.get(unitPlan, "templateId", null);

      const unitPlanDetails = getUnitDetailsFromUnitPlanField({
        assessmentDetails,
      });

      const { unitPlanFields } = unitPlanDetails || {};

      const resolvedSubjectNodes = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      let templates = getPlannerTemplateFromCache(
        {
          ids: getTemplateArray([templateId]),
        },
        true
      );

      if (mode !== "view" && !_.isEmpty(templates)) {
        const unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
        templates = [...templates, unitPlanTemplate];
      }

      if (_.isEmpty(templates)) {
        templates = getPlannerTemplateFromCache(
          {
            ids: getTemplateArray([templateId]),
          },
          true
        );
      }

      const originalTemplate = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const isFromClassroom = getIsFromClassroom({
        sourceFrom,
        template: originalTemplate,
      });

      const dynamicTemplate = updateTemplateForLearningGoals({
        template: _.cloneDeep(originalTemplate),
        templateId,
      });

      const localisedAssessmentTemplate = _.get(
        getLocalisedTemplate({
          template: { body: _.cloneDeep(dynamicTemplate) },
          t,
          templateType: "le",
        }),
        "body",
        {}
      );

      /** As we have learning goals on question level as well, so we are mutating learning goals fields in assessmentDetail.allFields/unitPlanDetails.unitPlanFields and adding question's learningGoals fields */
      if (isQuestionAssistant) {
        const updatedAssessmentFields = replaceLearningGoalsFields({
          template: dynamicTemplate,
          fields: _.get(assessmentDetails, "allFields", []),
          replacementFields: _.get(conversationMetadata, "allFields", []),
        });

        assessmentDetails = update(assessmentDetails, {
          allFields: {
            $set: updatedAssessmentFields,
          },
        });
      }

      const assessmentTemplate = updateAssessmentTemplate({
        t,
        template: localisedAssessmentTemplate,
        assessmentType,
        isFromClassroom,
        curriculumType,
        resourceFields: _.get(assessmentDetails, "allFields", []),
        mode: "edit",
        templateId,
        plannerElementSets,
        subjects: _.get(course, "subjects", []),
        assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
      });

      let _unitPlanTemplate = _.find(templates, { id: unitPlanTemplateId });

      if (_.isEmpty(_unitPlanTemplate) && !_.isEmpty(unitPlanTemplateId)) {
        _unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
      }

      const unitPlanTemplate = _.get(
        updateUnitPlanTemplate({
          t,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: unitPlanDetails?.unitPlanFields,
            uid: "unitType",
          }),
          curriculumType,
          resolvedSubjectNodes,
        }),
        "body",
        {}
      );

      const learningGoalsFields = getLearningGoalsFields(assessmentTemplate);

      return {
        messages,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        conversations: queryData,
        isInitialState: _.isEmpty(messages),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        loadMoreFeed: prepareLoadMoreFeed({
          query: getAIConversationsQuery,
          variables: {
            ...getAIConversations.variables,
            after: endCursor,
          },
          queryData: getAIConversations,
          key: "messages",
        }),
        conversationMetadata,
        conversationAttachments,
        assessmentType,
        gradeId,
        course,
        groupType,
        unitPlanDetails: {
          ...unitPlanDetails,
          unitPlanFields: _.uniqBy(
            [
              ...combineUnitPlanMultiFieldData({
                unitPlanFields: _.cloneDeep(
                  _.get(unitPlanDetails, "unitPlanFields", [])
                ),
                templateBody: assessmentTemplate,
              }),
              ..._.get(unitPlanDetails, "unitPlanFields", []),
            ],
            "uid"
          ),
        },
        unitPlanTemplate,
        assessmentTemplate,
        learningGoalsFields,
        assessmentDetails,
        curriculumType,
        templateId,
      };
    },
  }),
  getAssessmentQuestionsEnhancer(),
  getAssessmentQuestionsEnhancerV2(),
  withLoader
)(AiQuestionAssistant);
